"use client";

import { cn } from "@/lib/utils";
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  Pause,
  Activity,
  Database,
  FileCheck,
  Cpu,
} from "lucide-react";
import type { OperationalStateContract } from "@/src/data/operational-state";

interface TruthItemProps {
  label: string;
  value: string;
  status:
    | "blocker"
    | "pending"
    | "active"
    | "synced"
    | "incomplete"
    | "paused"
    | "waiting"
    | "stable"
    | "warning"
    | "critical";
}

function TruthItem({ label, value, status }: TruthItemProps) {
  const statusConfig = {
    blocker: {
      color: "text-destructive",
      bgColor: "bg-destructive/10",
      icon: <AlertTriangle className="w-3.5 h-3.5" />,
    },
    pending: {
      color: "text-warning",
      bgColor: "bg-warning/10",
      icon: <Clock className="w-3.5 h-3.5" />,
    },
    active: {
      color: "text-accent",
      bgColor: "bg-accent/10",
      icon: <Activity className="w-3.5 h-3.5" />,
    },
    synced: {
      color: "text-success",
      bgColor: "bg-success/10",
      icon: <CheckCircle2 className="w-3.5 h-3.5" />,
    },
    incomplete: {
      color: "text-warning",
      bgColor: "bg-warning/10",
      icon: <FileCheck className="w-3.5 h-3.5" />,
    },
    paused: {
      color: "text-muted-foreground",
      bgColor: "bg-muted",
      icon: <Pause className="w-3.5 h-3.5" />,
    },
    waiting: {
      color: "text-muted-foreground",
      bgColor: "bg-muted",
      icon: <Clock className="w-3.5 h-3.5" />,
    },
    stable: {
      color: "text-success",
      bgColor: "bg-success/10",
      icon: <Cpu className="w-3.5 h-3.5" />,
    },
    warning: {
      color: "text-warning",
      bgColor: "bg-warning/10",
      icon: <AlertTriangle className="w-3.5 h-3.5" />,
    },
    critical: {
      color: "text-destructive",
      bgColor: "bg-destructive/10",
      icon: <AlertTriangle className="w-3.5 h-3.5" />,
    },
  };

  const config = statusConfig[status];

  return (
    <div className="flex items-center justify-between py-2.5 px-3 border-b border-border/50 last:border-b-0 hover:bg-muted/30 transition-colors">
      <div className="flex items-center gap-2">
        <span className={cn("", config.color)}>{config.icon}</span>
        <span className="text-xs text-muted-foreground">{label}</span>
      </div>
      <span
        className={cn(
          "text-xs font-medium px-2 py-0.5 rounded",
          config.color,
          config.bgColor
        )}
      >
        {value}
      </span>
    </div>
  );
}

interface OperationalTruthRailProps {
  truthRail: OperationalStateContract["truthRail"];
  onToggleDeepContext?: () => void;
}

export function OperationalTruthRail({
  truthRail,
  onToggleDeepContext,
}: OperationalTruthRailProps) {
  const rows = [
    truthRail.blockers,
    truthRail.approvals,
    truthRail.activeJobs,
    truthRail.memory,
    truthRail.readiness,
    truthRail.codexQueue,
    truthRail.claudeReview,
    truthRail.runtime,
  ];

  return (
    <aside className="hidden w-64 shrink-0 border-l border-border bg-card/50 lg:flex lg:flex-col">
      <div className="p-3 border-b border-border">
        <h2 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
          Operational Truth
        </h2>
      </div>
      <div className="flex-1 overflow-y-auto">
        {rows.map((row) => (
          <TruthItem
            key={row.label}
            label={row.label}
            value={row.value}
            status={row.status}
          />
        ))}
      </div>

      {/* Deep Context Collapsed State */}
      <div className="border-t border-border p-3">
        <button
          onClick={onToggleDeepContext}
          className="w-full flex items-center justify-between text-xs text-muted-foreground hover:text-foreground transition-colors"
        >
          <span className="flex items-center gap-2">
            <Database className="w-3.5 h-3.5" />
            {truthRail.deepContext.label}
          </span>
          <span className="text-[10px] bg-muted px-1.5 py-0.5 rounded">
            {truthRail.deepContext.value}
          </span>
        </button>
      </div>
    </aside>
  );
}
